/**
 * Stat-account drill-downs under the Occupied (ORN) and ADR rows — one row
 * per Occupied Rooms stat account, showing the room nights (or ADR) flowing
 * into each month and at what share. Resolved by the same rules the raw data
 * export uses (resolveStatAccountMonths): GL actuals on actual months, room
 * nights keyed in stat-account mode as keyed, otherwise the ORN spread pro
 * rata to each segment's linked room-revenue forecast.
 *
 * Both rows share ONE plan (`useStatAccountPlan`): the same fetch, the same
 * resolution, and one commit path. Editing an RN or ADR cell writes the whole
 * month as a stat-account plan through applyRevenueForecast (stat_account
 * mode) — exactly what the revenue modal does — so RN × ADR lands as revenue
 * overrides on the linked accounts and Σ RN as the ORN override.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { getStatAccountExport, applyRevenueForecast } from '../../api';
import type { StatAccountExportRow, StatAccountInput } from '../../api';
import { resolveStatAccountMonths } from '../../utils/forecastRawExport';
import type { RawExportRoomStats, StatLeaf, StatLeafSource } from '../../utils/forecastRawExport';
import type { ForecastAccount } from '../../types';
import { MONTHS } from '../../types';
import { formatCurrency } from '../../utils/formatters';
import { currencySymbol } from '../../../../utils/currency';
import type { ExpandedColumnDef } from './columnRegistry';
import { TOTAL_COLUMN_INDEX } from './columnRegistry';

export type StatMetric = 'rn' | 'adr';

export interface StatAccountPlanOptions {
  enabled: boolean;
  orgId?: number | null;
  locationId?: number | null;
  versionId?: number | string | null;
  year?: number;
  roomStats: RawExportRoomStats;
  revenueAccounts: ForecastAccount[];
  actualMonthKeys: Set<string>;
  /** Bump to refetch (e.g. after a recalculation) while expanded. */
  refreshKey?: unknown;
  isReadOnly?: boolean;
  onApplied?: () => void | Promise<void>;
}

export interface StatAccountPlan {
  loading: boolean;
  error: string | null;
  leaves: StatLeaf[];
  /** Screen ORN per month (null when unknown). */
  occupied: (number | null)[];
  /** Σ segment room nights per month. */
  rnTotals: number[];
  /** Month where the segments' total ≠ the ORN row. */
  mismatch: boolean[];
  revenueById: Map<number, ForecastAccount>;
  /** Room nights for a segment-month (null when blank). */
  rnOf: (leaf: StatLeaf, monthIdx: number) => number | null;
  /** ADR for a segment-month: keyed ADR, else linked revenue ÷ room nights, else null. */
  adrOf: (leaf: StatLeaf, monthIdx: number) => number | null;
  /** Revenue a segment-month implies (RN × keyed ADR, or the linked account's on-screen value). */
  revenueOf: (leaf: StatLeaf, monthIdx: number) => number | null;
  sourceOf: (leaf: StatLeaf, monthIdx: number) => StatLeafSource | null;
  canEdit: boolean;
  isEditableMonth: (leaf: StatLeaf, monthIdx: number) => boolean;
  savingMonth: number | null;
  commit: (leaf: StatLeaf, monthIdx: number, patch: { rn?: number; adr?: number }) => Promise<void>;
}

const round2 = (v: number) => Math.round(v * 100) / 100;
const keyedAdrOf = (leaf: StatLeaf, idx: number): number | null => {
  const v = leaf.account.adrInputs?.[idx + 1] ?? leaf.account.adrInputs?.[String(idx + 1)];
  return v === undefined || v === null ? null : Number(v);
};

export function useStatAccountPlan({
  enabled,
  orgId,
  locationId,
  versionId,
  year,
  roomStats,
  revenueAccounts,
  actualMonthKeys,
  refreshKey,
  isReadOnly = false,
  onApplied,
}: StatAccountPlanOptions): StatAccountPlan {
  const [statAccounts, setStatAccounts] = useState<StatAccountExportRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingMonth, setSavingMonth] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled || !orgId || !locationId || !year) return;
    let cancelled = false;
    setError(null);
    getStatAccountExport({ orgId, locationId, versionId, year })
      .then((res) => { if (!cancelled) setStatAccounts(res.accounts || []); })
      .catch((err) => {
        console.error('[StatAccountPlan] fetch failed', err);
        if (!cancelled) setError('Stat accounts unavailable');
      });
    return () => { cancelled = true; };
  }, [enabled, orgId, locationId, versionId, year, refreshKey]);

  const resolved = useMemo(() => {
    if (!statAccounts) return null;
    return resolveStatAccountMonths({
      actualMonthKeys,
      revenueDepartments: [{ department: 'Revenue', accounts: revenueAccounts, monthlyTotals: {}, total: 0 }],
      roomStats,
      statAccounts,
    });
  }, [statAccounts, actualMonthKeys, revenueAccounts, roomStats]);

  const revenueById = useMemo(() => new Map(revenueAccounts.map((a) => [a.chartOfAccountId, a])), [revenueAccounts]);

  const leaves = useMemo(() => {
    if (!resolved) return [];
    return [...resolved.occupiedLeaves].sort(
      (a, b) =>
        a.account.accountNumber.localeCompare(b.account.accountNumber, undefined, { numeric: true }) ||
        a.account.accountName.localeCompare(b.account.accountName)
    );
  }, [resolved]);

  const occupied = useMemo<(number | null)[]>(() => resolved?.occupied ?? MONTHS.map(() => null), [resolved]);
  const rnTotals = useMemo(() => MONTHS.map((_, idx) => leaves.reduce<number>((s, l) => s + (l.months[idx] ?? 0), 0)), [leaves]);
  const mismatch = useMemo(
    () => MONTHS.map((_, idx) => {
      const orn = occupied[idx];
      return orn !== null && leaves.some((l) => l.months[idx] !== null) && Math.round(rnTotals[idx]) !== Math.round(orn);
    }),
    [leaves, occupied, rnTotals]
  );

  const rnOf = useCallback((leaf: StatLeaf, idx: number) => leaf.months[idx], []);
  const sourceOf = useCallback((leaf: StatLeaf, idx: number) => leaf.sources[idx], []);
  const linkedRevenueOf = useCallback((leaf: StatLeaf, idx: number): number | null => {
    if (leaf.account.revenueChartOfAccountId == null) return null;
    const v = revenueById.get(leaf.account.revenueChartOfAccountId)?.monthlyValues?.[MONTHS[idx]];
    return typeof v === 'number' ? Math.max(0, v) : null;
  }, [revenueById]);
  const adrOf = useCallback((leaf: StatLeaf, idx: number): number | null => {
    const rn = leaf.months[idx];
    if (leaf.sources[idx] === 'keyed') {
      const keyed = keyedAdrOf(leaf, idx);
      if (keyed !== null) return keyed;
    }
    const revenue = linkedRevenueOf(leaf, idx);
    if (rn === null || !(rn > 0) || revenue === null) return null;
    return revenue / rn;
  }, [linkedRevenueOf]);
  const revenueOf = useCallback((leaf: StatLeaf, idx: number): number | null => {
    const rn = leaf.months[idx];
    if (leaf.sources[idx] === 'keyed') {
      const keyed = keyedAdrOf(leaf, idx);
      if (keyed !== null && rn !== null) return rn * keyed;
    }
    return linkedRevenueOf(leaf, idx);
  }, [linkedRevenueOf]);

  const canEdit = !isReadOnly && !!onApplied && !!versionId && !!orgId && !!locationId && !!year;
  const isEditableMonth = useCallback(
    (leaf: StatLeaf, idx: number) =>
      canEdit && !actualMonthKeys.has(MONTHS[idx]) && leaf.account.revenueChartOfAccountId != null && leaf.account.statAccountId != null,
    [canEdit, actualMonthKeys]
  );

  const commit = useCallback(async (target: StatLeaf, monthIdx: number, patch: { rn?: number; adr?: number }) => {
    if (!resolved || !canEdit || !year) return;
    if (target.account.revenueChartOfAccountId == null) {
      toast.error('This stat account is not linked to a room-revenue account, so it cannot carry a plan.');
      return;
    }
    const monthNumber = monthIdx + 1;
    const linkedLeaves = resolved.occupiedLeaves.filter((l) => l.account.revenueChartOfAccountId != null && l.account.statAccountId != null);
    const orn = resolved.occupied[monthIdx] ?? 0;
    const roomsRevenue = linkedLeaves.reduce<number>((sum, l) => sum + (linkedRevenueOf(l, monthIdx) ?? 0), 0);
    const monthAdr = orn > 0 ? roomsRevenue / orn : 0;

    // Every linked segment at its current value (keyed or pro-rata), the
    // edited one patched — the month becomes a keyed stat-account plan.
    const statAccountInputs: Record<number, StatAccountInput> = {};
    for (const leaf of linkedLeaves) {
      const saId = leaf.account.statAccountId as number;
      const isTarget = leaf.account.chartOfAccountId === target.account.chartOfAccountId;
      const currentRn = Math.max(0, Math.round(leaf.months[monthIdx] ?? 0));
      const currentAdr = adrOf(leaf, monthIdx) ?? monthAdr;
      const rn = isTarget && patch.rn !== undefined ? patch.rn : currentRn;
      const adr = isTarget && patch.adr !== undefined ? patch.adr : currentAdr;
      statAccountInputs[saId] = {
        rn: Math.max(0, Math.round(rn)),
        adr: round2(Math.max(0, adr)),
        chartOfAccountId: leaf.account.chartOfAccountId,
        revenueChartOfAccountId: leaf.account.revenueChartOfAccountId ?? undefined,
      };
    }
    const linkedAccount = revenueById.get(target.account.revenueChartOfAccountId);
    const departmentName = target.account.revenueDepartment || linkedAccount?.department || 'Rooms';
    const chartOfAccountDepartmentId = target.account.revenueDepartmentId ?? linkedAccount?.departmentId ?? undefined;
    const targetInput = statAccountInputs[target.account.statAccountId as number];
    const before = revenueOf(target, monthIdx) ?? 0;
    const after = targetInput.rn * targetInput.adr;

    setSavingMonth(monthIdx);
    try {
      await applyRevenueForecast({
        versionId: Number(versionId),
        locationId: Number(locationId),
        orgId: Number(orgId),
        year,
        month: monthNumber,
        departmentName,
        chartOfAccountDepartmentId,
        inputs: { groupRN: 0, groupADR: 0, transRN: 0, transADR: 0 },
        dailyData: [],
        spreadMethod: 'even',
        distribution: [],
        distributionMethod: 'manual',
        mode: 'stat_account',
        statAccountInputs,
        occupiedRoomNights: Object.values(statAccountInputs).reduce((sum, i) => sum + i.rn, 0),
      });
      const delta = after - before;
      toast.success(
        `${target.account.accountName}: ${targetInput.rn.toLocaleString()} RN × ${formatCurrency(targetInput.adr, false)} → ${linkedAccount?.accountName || 'linked revenue'} ${delta >= 0 ? '+' : '−'}${formatCurrency(Math.abs(delta), false)}`
      );
      await onApplied?.();
    } catch (err) {
      console.error('[StatAccountPlan] apply failed', err);
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(message || 'Failed to apply stat-account plan');
    } finally {
      setSavingMonth(null);
    }
  }, [resolved, canEdit, year, versionId, locationId, orgId, linkedRevenueOf, adrOf, revenueOf, revenueById, onApplied]);

  return {
    loading: enabled && !resolved && !error,
    error,
    leaves,
    occupied,
    rnTotals,
    mismatch,
    revenueById,
    rnOf,
    adrOf,
    revenueOf,
    sourceOf,
    canEdit,
    isEditableMonth,
    savingMonth,
    commit,
  };
}

// ── Rows ────────────────────────────────────────────────────────────────────

interface StatAccountBreakdownRowsProps {
  plan: StatAccountPlan;
  metric: StatMetric;
  actualMonthKeys: Set<string>;
  isBulkMode?: boolean;
  isColumnExpanded?: (monthIndex: number) => boolean;
  activeColumns?: ExpandedColumnDef[];
  currency?: string | null;
  /** Open the stat-account mapping (revenue modal) for the segments' department. */
  onOpenStatMapping?: (dept?: { department: string; departmentId?: number }) => void;
}

const SOURCE_STYLE: Record<StatLeafSource, { label: string; color: string }> = {
  actual: { label: 'GL actual', color: 'var(--lodge-onyx-500)' },
  keyed: { label: 'Keyed stat-account plan', color: 'var(--lodge-orange-500)' },
  prorata: { label: 'Pro rata by room revenue', color: 'var(--lodge-onyx-400)' },
};

const fmtInt = (v: number | null) => (v === null ? '—' : Math.round(v).toLocaleString('en-US'));
const fmtPct = (v: number | null) => (v === null ? '' : `${v.toFixed(1)}%`);
const fmtAdr = (v: number | null, currency?: string | null) =>
  v === null ? '—' : `${currencySymbol(currency)}${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function StatAccountBreakdownRows({
  plan,
  metric,
  actualMonthKeys,
  isBulkMode = false,
  isColumnExpanded,
  activeColumns = [],
  currency,
  onOpenStatMapping,
}: StatAccountBreakdownRowsProps) {
  const [editing, setEditing] = useState<{ chartOfAccountId: number; monthIdx: number } | null>(null);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) { inputRef.current?.focus(); inputRef.current?.select(); } }, [editing]);

  const { leaves, occupied, rnTotals, mismatch, revenueById } = plan;
  // The Total column may be expanded into sub-columns; span it as one cell.
  const totalColSpan = isColumnExpanded?.(TOTAL_COLUMN_INDEX) ? Math.max(1, activeColumns.length) : 1;

  const labelCell = (content: React.ReactNode, muted = false) => (
    <td
      className={`${isBulkMode ? '' : 'sticky left-0'} bg-white py-1 px-4 pl-12 text-[11px] z-20 group-hover:bg-slate-50 ${muted ? 'text-slate-400 italic' : 'text-slate-500'}`}
    >
      {content}
    </td>
  );
  const spanCells = (content: React.ReactNode) => {
    const count = MONTHS.reduce((n, _, i) => n + (isColumnExpanded?.(i) ? Math.max(1, activeColumns.length) : 1), 0) + totalColSpan;
    return <td colSpan={count} className="py-1 px-3 text-[11px] text-slate-400">{content}</td>;
  };
  const noteRow = (label: React.ReactNode, body: React.ReactNode) => (
    <tr className="bg-white group">
      {isBulkMode && <td className="sticky left-0 bg-white z-20" />}
      {labelCell(label, true)}
      {spanCells(body)}
    </tr>
  );

  if (plan.error) return noteRow('Stat accounts', plan.error);
  if (plan.loading) return noteRow(<span className="inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" />Loading stat accounts…</span>, '');
  if (leaves.length === 0) {
    return noteRow('No Occupied Rooms stat accounts mapped', 'Map stat accounts to room revenue under Settings → Room Statistics to see the segment breakdown.');
  }

  const commitEdit = (leaf: StatLeaf, monthIdx: number, raw: string) => {
    setEditing(null);
    const parsed = Number(raw.replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(parsed)) return;
    const current = metric === 'rn' ? plan.rnOf(leaf, monthIdx) : plan.adrOf(leaf, monthIdx);
    const next = metric === 'rn' ? Math.max(0, Math.round(parsed)) : Math.max(0, round2(parsed));
    if (current !== null && Math.abs(current - next) < (metric === 'rn' ? 0.5 : 0.005)) return;
    void plan.commit(leaf, monthIdx, metric === 'rn' ? { rn: next } : { adr: next });
  };

  const rnGrand = rnTotals.reduce((a, b) => a + b, 0);
  // The department whose revenue accounts the segments link to.
  const mappingDept = (() => {
    const withDept = leaves.find((l) => l.account.revenueDepartment);
    if (withDept) return { department: withDept.account.revenueDepartment as string, departmentId: withDept.account.revenueDepartmentId ?? undefined };
    const linkedAcct = leaves.map((l) => (l.account.revenueChartOfAccountId != null ? revenueById.get(l.account.revenueChartOfAccountId) : undefined)).find(Boolean);
    return linkedAcct ? { department: linkedAcct.department, departmentId: linkedAcct.departmentId } : undefined;
  })();

  return (
    <>
      {leaves.map((leaf) => {
        const linked = leaf.account.revenueChartOfAccountId != null ? revenueById.get(leaf.account.revenueChartOfAccountId) : undefined;
        const rnTotal = leaf.months.reduce<number>((s, v) => s + (v ?? 0), 0);
        const revTotal = MONTHS.reduce<number>((s, _, i) => s + (plan.revenueOf(leaf, i) ?? 0), 0);
        const totalDisplay = metric === 'rn'
          ? (
            <div className="leading-tight">
              <div>{fmtInt(rnTotal)}</div>
              {rnGrand > 0 && <div className="text-[10px] text-slate-400">{fmtPct((rnTotal / rnGrand) * 100)}</div>}
            </div>
          )
          : <div>{fmtAdr(rnTotal > 0 ? revTotal / rnTotal : null, currency)}</div>;

        return (
          <tr key={`${metric}-${leaf.account.chartOfAccountId}`} className="bg-white hover:bg-slate-50 transition-colors group">
            {isBulkMode && <td className="sticky left-0 bg-white py-1 px-2 z-20 group-hover:bg-slate-50" />}
            {labelCell(
              <span
                className="inline-flex items-baseline gap-1.5 whitespace-nowrap"
                title={linked ? `Linked to ${linked.accountNumber} · ${linked.accountName}` : 'No linked room-revenue account — receives no pro-rata share'}
              >
                <span className="tabular-nums text-slate-400">{leaf.account.accountNumber}</span>
                <span>{leaf.account.accountName}</span>
                {!linked && (
                  onOpenStatMapping ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onOpenStatMapping(mappingDept); }}
                      className="text-[10px] text-amber-600 underline decoration-dotted underline-offset-2 hover:text-amber-700"
                      title="Open the stat-account mapping to link this segment to a revenue account"
                    >
                      unlinked
                    </button>
                  ) : <span className="text-[10px] text-amber-600">unlinked</span>
                )}
              </span>
            )}
            {MONTHS.map((monthKey, idx) => {
              const rn = plan.rnOf(leaf, idx);
              const adr = plan.adrOf(leaf, idx);
              const revenue = plan.revenueOf(leaf, idx);
              const source = plan.sourceOf(leaf, idx);
              const style = source ? SOURCE_STYLE[source] : null;
              const value = metric === 'rn' ? rn : adr;
              const denom = rnTotals[idx];
              const share = rn === null || !(denom > 0) ? null : (rn / denom) * 100;
              const editable = plan.isEditableMonth(leaf, idx);
              const isEditing = editing?.chartOfAccountId === leaf.account.chartOfAccountId && editing?.monthIdx === idx;
              const isSaving = plan.savingMonth === idx;
              const title = [
                style?.label,
                metric === 'rn' && share !== null ? `${fmtPct(share)} of ${fmtInt(denom)} room nights` : null,
                metric === 'adr' && rn !== null ? `${fmtInt(rn)} RN × ${fmtAdr(adr, currency)}` : null,
                revenue !== null && linked ? `${linked.accountName}: ${formatCurrency(revenue, true, currency)}` : null,
                metric === 'rn' && mismatch[idx] ? `Stat accounts total ${fmtInt(denom)} ≠ ORN ${fmtInt(occupied[idx])}` : null,
                editable ? (metric === 'rn' ? 'Click to set room nights' : 'Click to set ADR') : null,
              ].filter(Boolean).join(' · ');
              const startEdit = () => {
                if (!editable || isSaving) return;
                setEditing({ chartOfAccountId: leaf.account.chartOfAccountId, monthIdx: idx });
                setEditValue(value === null ? '' : metric === 'rn' ? String(Math.round(value)) : value.toFixed(2));
              };
              const cell = isEditing ? (
                <input
                  ref={inputRef}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={() => commitEdit(leaf, idx, editValue)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); commitEdit(leaf, idx, editValue); }
                    if (e.key === 'Escape') { e.preventDefault(); setEditing(null); }
                  }}
                  className="w-16 text-right text-[11px] tabular-nums border rounded px-1 py-0.5 outline-none"
                  style={{ borderColor: 'var(--lodge-orange-500)' }}
                />
              ) : (
                <div className={`leading-tight ${isSaving ? 'opacity-50' : ''}`}>
                  <div className="tabular-nums" style={{ color: style?.color || 'var(--lodge-onyx-400)' }}>
                    {metric === 'rn' ? fmtInt(value) : fmtAdr(value, currency)}
                  </div>
                  {metric === 'rn' && share !== null && (
                    <div className={`text-[10px] tabular-nums ${mismatch[idx] ? 'text-amber-600' : 'text-slate-400'}`}>
                      {fmtPct(share)}{mismatch[idx] ? ' ≠' : ''}
                    </div>
                  )}
                  {metric === 'adr' && revenue !== null && (
                    <div className="text-[10px] tabular-nums text-slate-400">{formatCurrency(revenue, false, currency)}</div>
                  )}
                </div>
              );
              const tdClass = `text-right py-1 text-[11px] ${editable ? 'cursor-pointer hover:bg-slate-100' : ''}`;
              if (isColumnExpanded?.(idx) && activeColumns.length > 0) {
                const targetKey = actualMonthKeys.has(monthKey) ? 'actual' : 'forecast';
                const targetIdx = Math.max(0, activeColumns.findIndex((c) => c.key === targetKey));
                return (
                  <React.Fragment key={monthKey}>
                    {activeColumns.map((col, ci) => (
                      <td
                        key={col.key}
                        className={ci === targetIdx ? `${tdClass} px-2` : 'py-1 px-2'}
                        title={ci === targetIdx ? title : undefined}
                        onClick={ci === targetIdx ? startEdit : undefined}
                      >
                        {ci === targetIdx ? cell : null}
                      </td>
                    ))}
                  </React.Fragment>
                );
              }
              return (
                <td key={monthKey} className={`${tdClass} px-3`} title={title} onClick={startEdit}>
                  {cell}
                </td>
              );
            })}
            <td colSpan={totalColSpan} className="text-right py-1 px-4 text-[11px] tabular-nums text-slate-500">{totalDisplay}</td>
          </tr>
        );
      })}
    </>
  );
}
